import { useLoadout } from "@/context/LoadoutContext";

/**
 * PageBackground — shared brand-tinted page background.
 *
 * Each client page picks either the "teal" (calm/tracking) or "orange"
 * (energy/discovery) variant. When the user has an active cosmetic theme
 * (bought a design from the Belohnungen store), BOTH variants are repainted
 * with the theme's palette so the whole app reads as a unified system that
 * follows the user's chosen colours (no yellow/orange bleeding through on
 * a purple/neon theme, no teal accents on a rose-gold theme, etc.).
 *
 * Wrap the OUTER `<div className="max-w-...">` INSIDE `<Layout>` with it.
 */
export default function PageBackground({ variant = "teal", children }) {
  const { palette, loadout } = useLoadout();
  const themed = !!loadout.active_theme;

  // When the user has an active cosmetic theme, BOTH variants follow the
  // palette so the whole app reads as a unified system. Without a theme,
  // the two variants keep their calm/energy semantics (teal / orange).
  if (themed) {
    // Slightly stronger for the "orange" variant to keep the sense of
    // energy/discovery vs. the calm "teal" pages.
    const alpha = variant === "orange" ? ["55", "28", "14"] : ["40", "20", "10"];
    return (
      <div
        className="min-h-screen"
        style={{
          backgroundImage: `linear-gradient(to bottom, ${palette.from}${alpha[0]} 0%, ${palette.via}${alpha[1]} 45%, ${palette.to}${alpha[2]} 100%)`,
        }}
      >
        {children}
      </div>
    );
  }

  if (variant === "orange") {
    return (
      <div className="min-h-screen bg-gradient-to-b from-[#FC9700]/30 via-[#FFEBCC]/70 to-[#FFEBCC]/25 dark:from-[#FC9700]/25 dark:via-[#2a1f10]/45 dark:to-[#2a1f10]/20">
        {children}
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#5A8B8F]/25 via-[#EAF3F4]/80 to-[#EAF3F4]/30 dark:from-[#5A8B8F]/25 dark:via-[#0e2b2c]/50 dark:to-[#0e2b2c]/25">
      {children}
    </div>
  );
}
